/**
 * BLRRD Ingest — uint8 sRGB ImageData → ACEScg Float32 (cached, once-per-load).
 *
 * Why this lives outside the per-frame kernel: per Codex review of the ACES
 * switch-over, the IDT pass costs ~24 MP × 4 LUT lookups + 1 matrix per
 * pixel. Doing that every render-frame burns the budget; doing it ONCE at
 * image-load (and again on crop/rebuild) is free in steady state.
 *
 * Pipeline:
 *   uint8 sRGB        → 256-entry sRGB-decode LUT       → linear-sRGB
 *   linear-sRGB       → IDT matrix (M_sRGB_to_ACEScg)   → ACEScg-linear
 *   alpha             → pass-through unchanged
 *
 * The 256-entry LUT is keyed on the original Uint8 source value (NOT a
 * Float32 quantize) so the decode is exact at every input level.
 *
 * Output buffer is Float32 RGBA, scene-referred linear in AP1 primaries.
 * Highlights may exceed 1.0 in concept (post-grade), though sRGB sources
 * never originate above 1.0 — values stay in [0,1] until the kernel
 * pushes them via brightness/grade.
 */

import {
    M_sRGB_to_ACEScg,
    ACES_AP0_TO_ACEScg,
    BT2020_TO_ACEScg,
    decodeSRGB,
} from '../color/spaces.js';

/* 256-entry LUT: U8 sRGB → linear-sRGB (Float32). Computed once at module
 * load. Memory cost: 1 KB. Cheaper and more accurate than quantizing to a
 * larger float-side LUT. */
const SRGB_DECODE_U8_LUT = (() => {
    const lut = new Float32Array(256);
    for (let i = 0; i < 256; i++) lut[i] = decodeSRGB(i / 255);
    return lut;
})();

/**
 * Convert a Uint8ClampedArray (RGBA, sRGB-encoded) into Float32 ACEScg.
 *
 * @param {Uint8ClampedArray | Uint8Array} u8     sRGB RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} [dst]                    optional reusable buffer
 * @returns {Float32Array}                        ACEScg-linear RGBA, same length
 */
export function ingestSRGB(u8, w, h, dst) {
    const N = w * h * 4;
    const out = dst && dst.length === N ? dst : new Float32Array(N);
    const m = M_sRGB_to_ACEScg;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    for (let i = 0; i < N; i += 4) {
        const r = SRGB_DECODE_U8_LUT[u8[i]];
        const g = SRGB_DECODE_U8_LUT[u8[i + 1]];
        const b = SRGB_DECODE_U8_LUT[u8[i + 2]];
        out[i]     = m0 * r + m1 * g + m2 * b;
        out[i + 1] = m3 * r + m4 * g + m5 * b;
        out[i + 2] = m6 * r + m7 * g + m8 * b;
        out[i + 3] = u8[i + 3] / 255;
    }
    return out;
}

/**
 * Re-ingest from a cropped Uint8 buffer. Same as ingestSRGB but the caller
 * has already extracted the cropped region — we just translate space.
 * Kept as a separate export so call-sites at crop time read clearly.
 */
export const ingestCroppedSRGB = ingestSRGB;

/**
 * Inject an already-linear ACEScg Float32 buffer into the editor pipeline.
 *
 * Used by the HDR merge path (Mertens fusion outputs ACEScg-linear directly,
 * so we must NOT run the sRGB→ACEScg matrix again). This is a copy-only
 * passthrough that:
 *   - validates length matches (w*h*4)
 *   - copies into `dst` if provided (or allocates a new Float32Array)
 *   - does NOT clamp — values may slightly exceed [0,1] from band reconstruction
 *
 * @param {Float32Array} rgbaFloat32   ACEScg-linear RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} [dst]         optional reusable buffer
 * @returns {Float32Array}             same data, copied into dst or new allocation
 */
export function ingestLinearACEScg(rgbaFloat32, w, h, dst) {
    const N = w * h * 4;
    if (!rgbaFloat32 || rgbaFloat32.length !== N) {
        throw new Error('ingestLinearACEScg: buffer length mismatch');
    }
    const out = dst && dst.length === N ? dst : new Float32Array(N);
    /* Float32→Float32 copy. Use .set when possible for the typed-array fast path. */
    if (out !== rgbaFloat32) out.set(rgbaFloat32);
    return out;
}

/**
 * Ingest a linear-Rec.709 (== linear-sRGB primaries, linear gamma) Float32
 * buffer into ACEScg. Used by 16-bit TIFF input (Phase 3.A.1) where the
 * decoder output is already linearised in display primaries but needs the
 * sRGB→ACEScg matrix to land in our working space.
 *
 * Skips the sRGB-decode LUT (input is already linear) and runs only the
 * matrix step. Per-channel cost: 3 mul + 2 add. Same as ingestSRGB inner
 * loop minus the LUT lookup.
 *
 * @param {Float32Array} rgbaFloat32   linear-Rec.709 RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} [dst]         optional reusable buffer
 * @returns {Float32Array}             ACEScg-linear RGBA
 */
/**
 * Ingest an sRGB-encoded Float32 buffer (e.g. 16-bit TIFF normalized to
 * [0, 1] but still gamma-encoded) into ACEScg. Phase 3.A.1 — most 16-bit
 * TIFFs from Lightroom/Photoshop are sRGB-encoded, not linear, so we
 * apply the sRGB transfer-function inverse per channel before the
 * matrix step (Codex r1 STOP-3).
 *
 * Per-channel cost: 1 sRGB-decode (closed-form, branched) + 3 mul + 2 add.
 * For 24MP × 4ch ≈ 100M ops once at load — sub-second on Apple Silicon.
 *
 * @param {Float32Array} rgbaFloat32   sRGB-encoded RGBA in [0, 1]
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} [dst]
 * @returns {Float32Array}             ACEScg-linear RGBA
 */
export function ingestSRGBFloat(rgbaFloat32, w, h, dst) {
    const N = w * h * 4;
    if (!rgbaFloat32 || rgbaFloat32.length !== N) {
        throw new Error('ingestSRGBFloat: buffer length mismatch');
    }
    const out = dst && dst.length === N ? dst : new Float32Array(N);
    const m = M_sRGB_to_ACEScg;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    for (let i = 0; i < N; i += 4) {
        const r = decodeSRGB(rgbaFloat32[i]);
        const g = decodeSRGB(rgbaFloat32[i + 1]);
        const b = decodeSRGB(rgbaFloat32[i + 2]);
        out[i]     = m0 * r + m1 * g + m2 * b;
        out[i + 1] = m3 * r + m4 * g + m5 * b;
        out[i + 2] = m6 * r + m7 * g + m8 * b;
        out[i + 3] = rgbaFloat32[i + 3];
    }
    return out;
}

export function ingestLinearRec709(rgbaFloat32, w, h, dst) {
    const N = w * h * 4;
    if (!rgbaFloat32 || rgbaFloat32.length !== N) {
        throw new Error('ingestLinearRec709: buffer length mismatch');
    }
    const out = dst && dst.length === N ? dst : new Float32Array(N);
    const m = M_sRGB_to_ACEScg;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    for (let i = 0; i < N; i += 4) {
        const r = rgbaFloat32[i];
        const g = rgbaFloat32[i + 1];
        const b = rgbaFloat32[i + 2];
        out[i]     = m0 * r + m1 * g + m2 * b;
        out[i + 1] = m3 * r + m4 * g + m5 * b;
        out[i + 2] = m6 * r + m7 * g + m8 * b;
        out[i + 3] = rgbaFloat32[i + 3];
    }
    return out;
}

/**
 * Ingest a Float32 buffer that's in some camera-native primaries (e.g.
 * straight out of a libraw decode where we've decided to bypass the
 * baked-in sRGB matrix and apply a per-camera DCP-derived transform
 * instead).
 *
 * Two-stage matrix multiply, fused into one pass:
 *   1. cameraRGB → linear-Rec709 (the supplied 3×3 `mat`)
 *   2. linear-Rec709 → ACEScg     (M_sRGB_to_ACEScg)
 *
 * We pre-fold the two 3×3 matrices into a single 3×3 to keep the inner
 * loop at 9 muls + 6 adds per pixel — same cost as ingestLinearRec709
 * despite the camera-aware step.
 *
 * Phase 3.B-color P4: this is the wire-through point for DCP camera
 * profiles. `mat` is built by `src/pipeline/dcp/matrix.js
 * buildCameraToRec709(profile, t)` and passed in by the RAW decoder.
 *
 * @param {Float32Array} rgbaFloat32   cameraRGB RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {ArrayLike<number>} mat      row-major 3×3, cameraRGB → linear-Rec709
 * @param {Float32Array} [dst]
 * @returns {Float32Array}             ACEScg-linear RGBA
 */
/**
 * Ingest a linear ACES-AP0 (= ACES2065-1, archival/interchange) Float32
 * buffer into ACEScg. Used by Phase 3.C.1 EXR decode when the file's
 * `chromaticities` attribute declares AP0 primaries (~D60 whitepoint,
 * which matches AP1 — so the per-pixel cost is matrix-only, no Bradford
 * CAT round-trip).
 *
 * @param {Float32Array} rgbaFloat32   linear-AP0 RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} [dst]
 * @returns {Float32Array}             ACEScg-linear RGBA
 */
export function ingestLinearAP0(rgbaFloat32, w, h, dst) {
    const N = w * h * 4;
    if (!rgbaFloat32 || rgbaFloat32.length !== N) {
        throw new Error('ingestLinearAP0: buffer length mismatch');
    }
    const out = dst && dst.length === N ? dst : new Float32Array(N);
    const m = ACES_AP0_TO_ACEScg;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    for (let i = 0; i < N; i += 4) {
        const r = rgbaFloat32[i];
        const g = rgbaFloat32[i + 1];
        const b = rgbaFloat32[i + 2];
        out[i]     = m0 * r + m1 * g + m2 * b;
        out[i + 1] = m3 * r + m4 * g + m5 * b;
        out[i + 2] = m6 * r + m7 * g + m8 * b;
        out[i + 3] = rgbaFloat32[i + 3];
    }
    return out;
}

/**
 * Ingest a linear BT.2020 Float32 buffer into ACEScg. Used by Phase 3.C.1
 * EXR decode when `chromaticities` declares BT.2020 primaries (D65). The
 * matrix folds the Bradford CAT D65→D60 step internally.
 *
 * @param {Float32Array} rgbaFloat32   linear-BT.2020 RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} [dst]
 * @returns {Float32Array}             ACEScg-linear RGBA
 */
export function ingestLinearBT2020(rgbaFloat32, w, h, dst) {
    const N = w * h * 4;
    if (!rgbaFloat32 || rgbaFloat32.length !== N) {
        throw new Error('ingestLinearBT2020: buffer length mismatch');
    }
    const out = dst && dst.length === N ? dst : new Float32Array(N);
    const m = BT2020_TO_ACEScg;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    for (let i = 0; i < N; i += 4) {
        const r = rgbaFloat32[i];
        const g = rgbaFloat32[i + 1];
        const b = rgbaFloat32[i + 2];
        out[i]     = m0 * r + m1 * g + m2 * b;
        out[i + 1] = m3 * r + m4 * g + m5 * b;
        out[i + 2] = m6 * r + m7 * g + m8 * b;
        out[i + 3] = rgbaFloat32[i + 3];
    }
    return out;
}

export function ingestWithMatrix(rgbaFloat32, w, h, mat, dst) {
    const N = w * h * 4;
    if (!rgbaFloat32 || rgbaFloat32.length !== N) {
        throw new Error('ingestWithMatrix: buffer length mismatch');
    }
    if (!mat || mat.length !== 9) {
        throw new Error('ingestWithMatrix: mat must be a 9-element 3×3');
    }
    const out = dst && dst.length === N ? dst : new Float32Array(N);

    /* Pre-fold camera→Rec709 (`mat`) with Rec709→ACEScg (`M_sRGB_to_ACEScg`)
     * into a single camera→ACEScg matrix. Matches the kernel's preference
     * for fused 3×3 transforms — one pass, no intermediate buffer. */
    const A = M_sRGB_to_ACEScg;
    const B = mat;
    const c0 = A[0]*B[0] + A[1]*B[3] + A[2]*B[6];
    const c1 = A[0]*B[1] + A[1]*B[4] + A[2]*B[7];
    const c2 = A[0]*B[2] + A[1]*B[5] + A[2]*B[8];
    const c3 = A[3]*B[0] + A[4]*B[3] + A[5]*B[6];
    const c4 = A[3]*B[1] + A[4]*B[4] + A[5]*B[7];
    const c5 = A[3]*B[2] + A[4]*B[5] + A[5]*B[8];
    const c6 = A[6]*B[0] + A[7]*B[3] + A[8]*B[6];
    const c7 = A[6]*B[1] + A[7]*B[4] + A[8]*B[7];
    const c8 = A[6]*B[2] + A[7]*B[5] + A[8]*B[8];
    for (let i = 0; i < N; i += 4) {
        const r = rgbaFloat32[i];
        const g = rgbaFloat32[i + 1];
        const b = rgbaFloat32[i + 2];
        out[i]     = c0 * r + c1 * g + c2 * b;
        out[i + 1] = c3 * r + c4 * g + c5 * b;
        out[i + 2] = c6 * r + c7 * g + c8 * b;
        out[i + 3] = rgbaFloat32[i + 3];
    }
    return out;
}
